import { Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAlert from 'utils/alert';

const MAX_RENDERED_OPTIONS = 50;

const useStyles = makeStyles((theme: any) => ({
  hint: {
    padding: theme.spacing(1, 2),
  },
}));

const matchesSearch = (option: any, search: string) => option._id.includes(search)
	|| option.description.includes(search);

type AutocompleteVirtualizeProps = {
	options: any[];
	renderOption: (tag: any) => React.ReactNode;
	label: string;
	size?: 'small' | 'medium';
};

export const AutocompleteVirtualize = ({
  options, renderOption, label, size = 'medium',
}: AutocompleteVirtualizeProps) => {
  const [value] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const classes = useStyles();
  const showAlert = useAlert();
  const { t } = useTranslation();
  const copyTagCode = useCallback(async (tagId: string) => {
    try {
      const tagCode = `{{${tagId}}}`;
      await navigator.clipboard.writeText(tagCode);
      showAlert(t('documents:tag-copied'), 'success');
    } catch (err: any) {
      showAlert(err.message, 'error');
    }
  }, [showAlert, t]);

  const matchCount = useMemo(
    () => options.filter((option) => matchesSearch(option, inputValue.trim())).length,
    [options, inputValue]
  );

  const PaperWithHint = useMemo(() => {
    const Hinted = ({ children, ...rest }: React.HTMLAttributes<HTMLElement>) => (
      <Paper {...rest}>
        {children}
        {matchCount > MAX_RENDERED_OPTIONS && (
        <Typography variant="caption" color="textSecondary" className={classes.hint}>
          {t('documents:narrow-results', { count: MAX_RENDERED_OPTIONS })}
        </Typography>
        )}
      </Paper>
    );
    return Hinted;
  }, [matchCount, classes.hint, t]);

  return (
    <Autocomplete
      freeSolo
      forcePopupIcon
      style={{ width: '100%' }}
      size={size}
      options={options}
      PaperComponent={PaperWithHint}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
        />
      )}
      filterOptions={(optionsToFilter, search) => optionsToFilter
			  .filter((option) => matchesSearch(option, search.inputValue.trim()))
			  .slice(0, MAX_RENDERED_OPTIONS)}
      value={value}
      onChange={(event, newValue) => {
        if (newValue && typeof newValue !== 'string') {
          copyTagCode(newValue._id);
        }
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(newInputValue);
        }
      }}
      getOptionLabel={(tag: any) => tag.description}
      renderOption={(props, tag: any) => <li {...props}>{renderOption(tag)}</li>}
    />
  );
};
